import { Alert, Box, Collapse, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1CELEditor } from 'components/core/RHKFormComponents/C1CELEditor'
import { C1LabeledSelect } from 'components/core/RHKFormComponents/C1LabeledSelect'
import { TextField } from 'components/core/textfield'
import { EditorValidationEnv } from 'pbts/c1/api/editor/v1/editor'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Control, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import type { WorkloadSourcePreset } from './workloadSourcePresets'

type SourceTrustFieldsProps = {
    preset: WorkloadSourcePreset
    control: Control<any> // eslint-disable-line @typescript-eslint/no-explicit-any
    register: UseFormRegister<any> // eslint-disable-line @typescript-eslint/no-explicit-any
    watch: UseFormWatch<any> // eslint-disable-line @typescript-eslint/no-explicit-any
    setValue: UseFormSetValue<any> // eslint-disable-line @typescript-eslint/no-explicit-any
    errors: Record<string, { message?: string }>
}

/**
 * Renders per-source guided form fields from a preset's `fields` array.
 * Auto-generates the CEL expression and display name from field values.
 * Provides an "Edit expression manually" toggle that reveals C1CELEditor.
 */
export const SourceTrustFields: FC<SourceTrustFieldsProps> = ({
    preset,
    control,
    register,
    watch,
    setValue,
    errors,
}) => {
    const [manualMode, setManualMode] = useState(false)

    // Watch all preset field values to auto-generate CEL and display name.
    const fieldNames = useMemo(() => preset.fields.map((f) => f.name), [preset.fields])
    const watchedValues = watch(fieldNames)

    // Build a values map from watched field values.
    const valuesMap = useMemo(() => {
        const map: Record<string, string> = {}
        fieldNames.forEach((name, i) => {
            map[name] = (watchedValues[i] as string) ?? ''
        })
        return map
    }, [fieldNames, watchedValues])

    // Auto-generate CEL expression from guided fields.
    const generatedCEL = useMemo(() => {
        // Only generate if required fields have values.
        const requiredFilled = preset.fields
            .filter((f) => f.required)
            .every((f) => valuesMap[f.name]?.trim())
        if (!requiredFilled) return ''
        return preset.celTemplate(valuesMap)
    }, [preset, valuesMap])

    // Auto-generate display name.
    const generatedDisplayName = useMemo(() => {
        const requiredFilled = preset.fields
            .filter((f) => f.required)
            .every((f) => valuesMap[f.name]?.trim())
        if (!requiredFilled) return ''
        return preset.displayNameTemplate(valuesMap)
    }, [preset, valuesMap])

    // Sync generated values to form when in guided mode.
    useEffect(() => {
        if (!manualMode && generatedCEL) {
            setValue('conditionExpression', generatedCEL)
        }
    }, [generatedCEL, manualMode, setValue])

    useEffect(() => {
        if (generatedDisplayName) {
            setValue('displayName', generatedDisplayName)
        }
    }, [generatedDisplayName, setValue])

    const handleSwitchToManual = useCallback((): void => {
        setManualMode(true)
    }, [])

    // No guided fields for custom providers.
    if (preset.fields.length === 0) {
        return <Stack spacing={2}>
            <TextField
                label={<FormattedMessage id="wf_trust_display_name_label" defaultMessage="Display name" />}
                {...register('displayName')}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
                fullWidth
            />
            <Box>
                <Typography variant="body2" color="text.secondary" pb={1}>
                    <FormattedMessage
                        id="wf_trust_cel_label_custom"
                        defaultMessage="CEL condition expression"
                    />
                </Typography>
                <C1CELEditor
                    control={control}
                    name="conditionExpression"
                    error={!!errors.conditionExpression}
                    helperText={errors.conditionExpression?.message ?? 'Example: claims.sub == "my-subject" && claims.aud == "my-audience"'}
                    validationEnv={EditorValidationEnv.WORKLOAD_FEDERATION}
                    copilot
                    minHeight="100px"
                />
            </Box>
        </Stack>
    }

    return <Stack spacing={2}>
        {/* Guided fields */}
        {!manualMode && preset.fields.map((field) => {
            if (field.type === 'select' && field.selectOptions) {
                return <C1LabeledSelect
                    key={field.name}
                    control={control}
                    name={field.name}
                    label={field.label}
                    options={field.selectOptions.map((o) => ({
                        id: o.value,
                        value: o.value,
                        label: o.label,
                    }))}
                    error={!!errors[field.name]}
                    helperText={errors[field.name]?.message ?? field.helpText}
                    fullWidth
                />
            }
            return <TextField
                key={field.name}
                label={field.label}
                {...register(field.name)}
                error={!!errors[field.name]}
                helperText={errors[field.name]?.message ?? field.helpText}
                placeholder={field.placeholder}
                fullWidth
            />
        })}

        {/* Auto-generated display name (hidden, synced via useEffect) */}
        <TextField
            label={<FormattedMessage id="wf_trust_display_name_label" defaultMessage="Display name" />}
            {...register('displayName')}
            error={!!errors.displayName}
            helperText={errors.displayName?.message}
            fullWidth
        />

        {/* Security guidance */}
        {!manualMode && preset.securityGuidance && (
            <Alert severity="info">
                <Typography variant="body2">{preset.securityGuidance}</Typography>
            </Alert>
        )}

        {/* Generated CEL expression (read-only preview in guided mode) */}
        {!manualMode && generatedCEL && (
            <Box>
                <Typography variant="body2" color="text.secondary" pb={1}>
                    <FormattedMessage
                        id="wf_trust_generated_cel_label"
                        defaultMessage="Generated CEL expression"
                    />
                </Typography>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        backgroundColor: 'grey.50',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                    }}
                >
                    {generatedCEL}
                </Box>
                <C1Button
                    variant="text"
                    onClick={handleSwitchToManual}
                    sx={{ mt: 1, textTransform: 'none' }}
                >
                    <FormattedMessage
                        id="wf_trust_edit_manually"
                        defaultMessage="Edit expression manually"
                    />
                </C1Button>
            </Box>
        )}

        {/* Manual CEL editor (shown after toggle, one-way switch) */}
        <Collapse in={manualMode}>
            <Box>
                <Typography variant="body2" color="text.secondary" pb={1}>
                    <FormattedMessage
                        id="wf_trust_cel_label_manual"
                        defaultMessage="CEL condition expression"
                    />
                </Typography>
                <C1CELEditor
                    control={control}
                    name="conditionExpression"
                    error={!!errors.conditionExpression}
                    helperText={errors.conditionExpression?.message}
                    validationEnv={EditorValidationEnv.WORKLOAD_FEDERATION}
                    copilot
                    minHeight="100px"
                />
            </Box>
        </Collapse>
    </Stack>
}
